/**
 * AWS Step Functions State Language (STL) Converter
 *
 * Converts Workflow2 nodes/connections to AWS Step Functions definition format.
 * This module is responsible for transforming the visual workflow representation
 * into the JSON format required by AWS Step Functions.
 *
 * TODO: Complete implementation by converting nodes to STL format
 * Reference: Original implementation at src/pages/Workflow/components/index.js
 */

/* eslint-disable */

const AwsStlConverter = (function() {

  // AWS Lambda function prefix based on environment
  const getSiteStage = () => {
    const host = window.location.href;
    return host.includes('prod.teamio.app') ? 'prod' : 'dev';
  };

  // AWS Account ID (should match original)
  const AWS_ACCOUNT_ID = '581565885944';
  const AWS_REGION = 'us-east-1';

  // Base STL template structure
  const createStlTemplate = () => ({
    StartAt: "Try",
    States: {
      Try: {
        Type: "Parallel",
        Next: "UnwrapOutput",
        Branches: [
          {
            StartAt: "1",
            States: {}
          }
        ]
        // Note: Removed global Catch block - errors are now handled at the branch level
        // This allows parallel branches to fail independently without terminating other branches
      },
      UnwrapOutput: {
        Type: "Pass",
        InputPath: "$[0]",
        Next: "NotifySuccess"
      },
      NotifySuccess: {
        Type: "Pass",
        End: true
      }
    }
  });

  // Standard retry configuration
  const getRetryConfig = () => ([{
    "ErrorEquals": ["States.TaskFailed"],
    "IntervalSeconds": 1,
    "MaxAttempts": 2,
    "BackoffRate": 2.0
  }]);

  // Get Lambda function ARN
  const getLambdaArn = (functionName) => {
    const stage = getSiteStage();
    return `arn:aws:lambda:${AWS_REGION}:${AWS_ACCOUNT_ID}:function:${stage}-${functionName}`;
  };

  // Create state for different node types
  const createState = (node, siteID, workflowID, workflowTrigger, timezone) => {
    const siteStage = getSiteStage();
    const baseFunctionArn = getLambdaArn('workflow-service-send-msg-to-misc-msgs-queue');

    let template = null;

    switch (node.type) {
      case 'email':
        template = {
          Type: "Task",
          Resource: "arn:aws:states:::lambda:invoke",
          Parameters: {
            FunctionName: baseFunctionArn,
            Payload: {
              type: "EMAIL",
              siteID: siteID,
              timezone: timezone,
              workflowID: workflowID,
              workflowTrigger: workflowTrigger,
              fromName: node.properties?.fromName || '',
              fromEmail: node.properties?.fromEmail || '',
              onlyifpreviousunopened: node.properties?.onlyIfPreviousUnopened || 'false',
              properties: node.properties?.emailProperties || [],
              newPatientEmailTemplateID: node.properties?.newPatientEmailTemplateID || '',
              newPatientEmailMode: node.properties?.newPatientEmailMode || 'all',
              newPatientEmailProviderNums: node.properties?.newPatientEmailProviderNums || [],
              newPatientEmailRules: node.properties?.newPatientEmailRulesData || [],
              "originalEvent.$": "$",
              "executionID.$": "$$.Execution.Id"
            }
          },
          Retry: getRetryConfig(),
          TimeoutSeconds: 300,
          ResultPath: "$.TaskResult",
          OutputPath: "$"
        };
        break;

      case 'sms':
        template = {
          Type: "Task",
          Resource: "arn:aws:states:::lambda:invoke",
          Parameters: {
            FunctionName: baseFunctionArn,
            Payload: {
              type: "SMS",
              siteID: siteID,
              workflowID: workflowID,
              workflowTrigger: workflowTrigger,
              smsFrom: node.properties?.smsFrom || '',
              properties: node.properties?.smsProperties || [],
              newPatientSmsTemplateID: node.properties?.newPatientSmsTemplateID || '',
              newPatientMode: node.properties?.newPatientMode || 'all',
              newPatientProviderNums: node.properties?.newPatientProviderNums || [],
              newPatientRules: node.properties?.newPatientRulesData || [],
              "originalEvent.$": "$",
              "executionID.$": "$$.Execution.Id"
            }
          },
          Retry: getRetryConfig(),
          TimeoutSeconds: 300,
          ResultPath: "$.TaskResult",
          OutputPath: "$"
        };
        break;

      case 'assigntask':
        template = {
          Type: "Task",
          Resource: "arn:aws:states:::lambda:invoke",
          Parameters: {
            FunctionName: baseFunctionArn,
            Payload: {
              type: "ASSIGN_TASK",
              siteID: siteID,
              workflowID: workflowID,
              workflowTrigger: workflowTrigger,
              timezone: timezone,
              properties: node.properties?.taskProperties || [],
              "originalEvent.$": "$",
              "executionID.$": "$$.Execution.Id"
            }
          },
          Retry: getRetryConfig(),
          TimeoutSeconds: 300,
          ResultPath: "$.TaskResult",
          OutputPath: "$"
        };
        break;

      case 'wait':
        // Wait state can use Seconds (duration) or Timestamp (specific time)
        const waitForTime = node.properties?.waitForTime;
        const waitFor = node.properties?.waitFor;
        template = {
          Type: "Wait",
          ...(waitForTime === 'true' && { Timestamp: node.properties?.waitForTimestamp }),
          ...(waitFor === 'true' && { Seconds: parseInt(node.properties?.totalSeconds, 10) }),
          OutputPath: "$"
        };
        break;

      case 'sendform':
        const sendFormNewPatientOnly = node.properties?.sendFormNewPatientOnly === "true";
        const sendFormViaEmail = node.properties?.sendFormViaEmail === "true";
        const sendFormViaText = node.properties?.sendFormViaText === "true";

        template = {
          Type: "Task",
          Resource: "arn:aws:states:::lambda:invoke",
          Parameters: {
            FunctionName: baseFunctionArn,
            Payload: {
              type: "SEND_FORM",
              siteID: siteID,
              workflowID: workflowID,
              workflowTrigger: workflowTrigger,
              timezone: timezone,
              properties: node.properties?.formProperties || [],
              sendFormNewPatientOnly: sendFormNewPatientOnly,
              sendFormViaEmail: sendFormViaEmail,
              sendFormViaText: sendFormViaText,
              ...(sendFormNewPatientOnly && sendFormViaEmail && {
                sendFormNewPatientEmailTemplateID: node.properties?.emailTemplateId,
                sendFormNewPatientEmailSendWhen: node.properties?.emailSendWhen
              }),
              ...(sendFormNewPatientOnly && sendFormViaText && {
                sendFormNewPatientSmsTemplateID: node.properties?.smsTemplateId,
                sendFormNewPatientSmsSendWhen: node.properties?.smsSendWhen
              }),
              "originalEvent.$": "$",
              "executionID.$": "$$.Execution.Id"
            }
          },
          Retry: getRetryConfig(),
          TimeoutSeconds: 300,
          ResultPath: "$.TaskResult",
          OutputPath: "$"
        };
        break;

      case 'kanban':
        template = {
          Type: "Task",
          Resource: "arn:aws:states:::lambda:invoke",
          Parameters: {
            FunctionName: baseFunctionArn,
            Payload: {
              type: "KANBAN",
              siteID: siteID,
              workflowID: workflowID,
              workflowTrigger: workflowTrigger,
              timezone: timezone,
              properties: node.properties?.kanbanProperties || [],
              "originalEvent.$": "$",
              "executionID.$": "$$.Execution.Id"
            }
          },
          Retry: getRetryConfig(),
          TimeoutSeconds: 300,
          ResultPath: "$.TaskResult",
          OutputPath: "$"
        };
        break;

      case 'slack':
        template = {
          Type: "Task",
          Resource: "arn:aws:states:::lambda:invoke",
          Parameters: {
            FunctionName: baseFunctionArn,
            Payload: {
              type: "SLACK",
              siteID: siteID,
              workflowID: workflowID,
              workflowTrigger: workflowTrigger,
              timezone: timezone,
              slackChannelId: node.properties?.slackChannelId || '',
              slackChannelName: node.properties?.slackChannelName || '',
              slackMessage: node.properties?.slackMessage || '',
              "originalEvent.$": "$",
              "executionID.$": "$$.Execution.Id"
            }
          },
          Retry: getRetryConfig(),
          TimeoutSeconds: 300,
          ResultPath: "$.TaskResult",
          OutputPath: "$"
        };
        break;

      default:
        // Handle unknown types - return a pass state
        template = {
          Type: "Pass",
          Result: { type: node.type, message: "Unknown node type" },
          OutputPath: "$"
        };
        break;
    }

    return template;
  };

  // Find children nodes based on connections
  const getChildren = (nodes, connections, nodeId) => {
    const childConnections = connections.filter(c => c.source === nodeId);
    return childConnections.map(c => nodes.find(n => n.id === c.target)).filter(Boolean);
  };

  // Create a parallel state for multiple branches
  const createParallelState = (childNodes, allNodes, connections, siteID, workflowID, trigger, timezone) => {
    const parallel = {
      Type: "Parallel",
      End: true,
      Branches: []
    };

    for (const childNode of childNodes) {
      const branch = {
        StartAt: `state-${childNode.id}`,
        States: {}
      };

      let currentNode = childNode;
      let stateAdded = true;

      while (stateAdded) {
        const stateName = `state-${currentNode.id}`;
        const state = createState(currentNode, siteID, workflowID, trigger, timezone);

        const children = getChildren(allNodes, connections, currentNode.id);

        if (children.length === 0) {
          // For terminal states, create a Pass state to handle errors gracefully
          const errorHandlerName = `${stateName}-error-handler`;

          state.Next = errorHandlerName;
          // Add Catch block that continues to error handler
          if (state.Type === 'Task') {
            state.Catch = [{
              ErrorEquals: ["States.ALL"],
              ResultPath: "$.error",
              Next: errorHandlerName
            }];
          }
          branch.States[stateName] = state;

          // Add Pass state that ends the branch gracefully
          branch.States[errorHandlerName] = {
            Type: "Pass",
            End: true
          };

          stateAdded = false;
        } else if (children.length === 1) {
          state.Next = `state-${children[0].id}`;
          // Add Catch block to handle errors within this branch independently
          // When caught, continue to next state (same as normal flow)
          if (state.Type === 'Task') {
            state.Catch = [{
              ErrorEquals: ["States.ALL"],
              ResultPath: "$.error",
              Next: state.Next  // Match the state's Next transition
            }];
          }
          branch.States[stateName] = state;
          currentNode = children[0];
        } else {
          // Multiple children - create nested parallel
          const parallelStateName = `parallel-${children.map(c => c.id).join('-')}`;
          state.Next = parallelStateName;
          // Add Catch block to handle errors within this branch independently
          // When caught, continue to parallel state (same as normal flow)
          if (state.Type === 'Task') {
            state.Catch = [{
              ErrorEquals: ["States.ALL"],
              ResultPath: "$.error",
              Next: state.Next  // Match the state's Next transition
            }];
          }
          branch.States[stateName] = state;
          branch.States[parallelStateName] = createParallelState(
            children, allNodes, connections, siteID, workflowID, trigger, timezone
          );
          stateAdded = false;
        }
      }

      parallel.Branches.push(branch);
    }

    return parallel;
  };

  return {
    /**
     * Convert nodes and connections to AWS Step Functions State Language
     *
     * @param {Array} nodes - Array of workflow nodes
     * @param {Array} connections - Array of connections between nodes
     * @param {Object} options - Additional options (siteID, workflowID, trigger, timezone)
     * @returns {Object} AWS Step Functions definition
     */
    convert: function(nodes, connections, options = {}) {
      const { siteID, workflowID, trigger, timezone } = options;
      const stlTemplate = createStlTemplate();

      if (nodes.length === 0) {
        return stlTemplate;
      }

      // Find root nodes - either nodes with source: null in their connections (from trigger)
      // or nodes with no incoming connections
      const rootConnections = connections.filter(c => c.source === null);
      let rootNodes = [];

      if (rootConnections.length > 0) {
        // Use nodes that have connections from the trigger (source: null)
        rootNodes = rootConnections.map(c => nodes.find(n => n.id === c.target)).filter(n => n);
      } else {
        // Fallback: find nodes with no incoming connections
        const nodesWithIncoming = new Set(connections.map(c => c.target));
        const triggerNode = nodes.find(n => !nodesWithIncoming.has(n.id));
        if (triggerNode) {
          rootNodes = [triggerNode];
        }
      }

      if (rootNodes.length === 0) {
        console.error('No trigger node found in workflow');
        return stlTemplate;
      }

      // Get children of trigger (now we have direct root nodes)
      const children = rootNodes;

      if (children.length === 0) {
        // No actions after trigger - empty workflow
        return stlTemplate;
      }

      if (children.length === 1) {
        // Single branch - build linear state machine
        let currentNode = children[0];
        let stateIndex = 1;
        let continueLoop = true;

        while (continueLoop) {
          const state = createState(currentNode, siteID, workflowID, trigger, timezone);
          const nodeChildren = getChildren(nodes, connections, currentNode.id);

          if (nodeChildren.length === 0) {
            // For terminal states, create a Pass state to handle errors gracefully
            const errorHandlerName = `${stateIndex}-error-handler`;

            state.Next = errorHandlerName;
            // Add Catch block that continues to error handler
            if (state.Type === 'Task') {
              state.Catch = [{
                ErrorEquals: ["States.ALL"],
                ResultPath: "$.error",
                Next: errorHandlerName
              }];
            }
            stlTemplate.States.Try.Branches[0].States[stateIndex] = state;

            // Add Pass state that ends the branch gracefully
            stlTemplate.States.Try.Branches[0].States[errorHandlerName] = {
              Type: "Pass",
              End: true
            };

            continueLoop = false;
          } else if (nodeChildren.length === 1) {
            state.Next = (stateIndex + 1).toString();
            // Add Catch block to handle errors within this branch independently
            if (state.Type === 'Task') {
              state.Catch = [{
                ErrorEquals: ["States.ALL"],
                ResultPath: "$.error",
                Next: state.Next  // Match the state's Next transition
              }];
            }
            stlTemplate.States.Try.Branches[0].States[stateIndex] = state;
            currentNode = nodeChildren[0];
            stateIndex++;
          } else {
            // Multiple children - create parallel state
            state.Next = `parallel-${nodeChildren.map(c => c.id).join('-')}`;
            // Add Catch block to handle errors within this branch independently
            if (state.Type === 'Task') {
              state.Catch = [{
                ErrorEquals: ["States.ALL"],
                ResultPath: "$.error",
                Next: state.Next  // Match the state's Next transition
              }];
            }
            stlTemplate.States.Try.Branches[0].States[stateIndex] = state;

            const parallelState = createParallelState(
              nodeChildren, nodes, connections, siteID, workflowID, trigger, timezone
            );
            stlTemplate.States.Try.Branches[0].States[state.Next] = parallelState;
            continueLoop = false;
          }
        }
      } else {
        // Multiple children from trigger - start with parallel state
        const parallelState = createParallelState(
          children, nodes, connections, siteID, workflowID, trigger, timezone
        );
        stlTemplate.States.Try.Branches[0].States['1'] = parallelState;
      }

      return stlTemplate;
    },

    /**
     * Validate that the STL is well-formed
     *
     * @param {Object} stl - Step Functions definition to validate
     * @returns {Object} { valid: boolean, errors: string[] }
     */
    validate: function(stl) {
      const errors = [];

      if (!stl.StartAt) {
        errors.push('Missing StartAt property');
      }

      if (!stl.States || typeof stl.States !== 'object') {
        errors.push('Missing or invalid States property');
      }

      // Add more validation as needed

      return {
        valid: errors.length === 0,
        errors
      };
    }
  };

})();

export default AwsStlConverter;
